import { useEffect, RefObject } from 'react';
import { useLoggerWithContext } from '../services/logger/loggerContext';

interface UseExternalLinkTrackingOptions {
  /**
   * Container ref where the remote module is mounted
   */
  containerRef: RefObject<HTMLElement>;
  /**
   * Identifier for the module (e.g., app ID or name)
   */
  moduleId: string;
  /**
   * Base path of the module in PSP (e.g., /finance)
   */
  basePath: string;
  /**
   * Whether tracking is enabled
   */
  enabled?: boolean;
}

/**
 * Determines if a URL is external to the PSP shell context.
 *
 * A URL is considered external if:
 * 1. It has a different origin (protocol + host + port)
 * 2. It's an absolute URL to a different domain
 * 3. It's not a PSP route (doesn't start with a known base path)
 *
 * @param href - The href attribute from an anchor tag
 * @param currentOrigin - Current window origin
 * @returns true if the link takes user outside PSP context
 */
function isExternalLink(href: string, currentOrigin: string): boolean {
  try {
    // Skip non-http(s) protocols (mailto:, tel:, javascript:, etc.)
    if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) {
      return false;
    }

    // Relative URLs (/, ./, ../) are internal
    if (href.startsWith('/') || href.startsWith('./') || href.startsWith('../')) {
      return false;
    }

    // Hash-only links (#section) are internal
    if (href.startsWith('#')) {
      return false;
    }

    // Try to parse as absolute URL
    const url = new URL(href, currentOrigin);

    // Different origin = external
    if (url.origin !== currentOrigin) {
      return true;
    }

    return false;
  } catch {
    // If URL parsing fails, consider it internal to be safe
    return false;
  }
}

/**
 * Hook to track when remote modules contain links that navigate outside PSP context.
 *
 * Monitors click events on anchor tags within the remote module container and logs
 * when users click links that would take them out of the PSP shell (e.g., to standalone
 * application sites).
 *
 * This helps identify:
 * - Modules that aren't properly integrated
 * - Unintentional navigation away from PSP
 * - User flows that break the shell experience
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 * useExternalLinkTracking({
 *   containerRef,
 *   moduleId: 'finance-dashboard',
 *   basePath: '/finance',
 *   enabled: true,
 * });
 * ```
 */
export function useExternalLinkTracking(options: UseExternalLinkTrackingOptions) {
  const { containerRef, moduleId, basePath, enabled = true } = options;
  const logger = useLoggerWithContext();

  useEffect(() => {
    if (!enabled || !containerRef.current) {
      return;
    }

    const container = containerRef.current;
    const currentOrigin = window.location.origin;

    const handleClick = (event: MouseEvent) => {
      // Find the closest anchor tag (handles clicks on children of <a> tags)
      const anchor = (event.target as HTMLElement).closest('a');

      if (!anchor || !anchor.href) {
        return;
      }

      const href = anchor.getAttribute('href') || '';
      const target = anchor.getAttribute('target');

      // Check if this link is external to PSP
      if (isExternalLink(href, currentOrigin)) {
        logger.warn('Remote module initiated external navigation', {
          navigation_event: 'external_link_clicked',
          module_id: moduleId,
          module_base_path: basePath,
          link_href: href,
          link_text: anchor.textContent?.trim().substring(0, 100) || '',
          link_target: target || '_self',
          opens_new_tab: target === '_blank' || target === '_new',
          current_path: window.location.pathname,
          click_x: event.clientX,
          click_y: event.clientY,
        });
      }
    };

    // Use capture phase to catch the event before it bubbles
    container.addEventListener('click', handleClick, true);

    return () => {
      container.removeEventListener('click', handleClick, true);
    };
  }, [enabled, containerRef, moduleId, basePath, logger]);
}
